/**
 * Per-endpoint rate-limit governor for Bridge4IP / OCS API calls.
 *
 * LIMITS (per reseller, confirmed by Bridge4IP):
 *   Global:                          600 / min
 *   modifySubscriberMobilePlan:      300 / min
 *   AffectRecurringPackageToSub:     300 / min
 *   AffectSubscriberFakePhoneNumber: 300 / min
 *   AffectSubscriberRealPhoneNumber: 300 / min
 *   EsimStatusPerAccount:            300 / min
 *   GetSubscriberActivePeriod:       300 / min
 *   HlrGetBitrate / HlrSetBitrate:   300 / min
 *   ListSubscriber:                  300 / min
 *   ListSubscriberPrepaidPackages:   300 / min
 *   MoveSubscriberRangeToAccount:    300 / min
 *   PushSteeringToSubs:              300 / min
 *   SetSubscriberTrafficRestrictions:300 / min
 *   ListLocationZoneElement:         150 / min
 *   SendMtSms:                       150 / min
 *   SubscriberUsageOverPeriod:       100 / min  ← tight
 *   SubscriberNetworkEventsOverPeriod: 30 / min ← VERY TIGHT
 *   getSingleSubscriber (TBD):       300 / min  (assumed; alert on 429)
 *   getSubscriberLocationByCellId (TBD): 300 / min (assumed; alert on 429)
 *
 * CAPACITY RESERVATION:
 *   80% of each bucket is reserved for interactive (MCP tool) calls.
 *   20% is reserved for batch / cron callers (ocs-bridge polls).
 *   Callers pass `priority: "interactive" | "batch"` to acquireEndpointSlot().
 *   Interactive callers draw from the full bucket capacity.
 *   Batch callers can only draw up to 20% of the per-minute limit per minute.
 *
 * ALGORITHM:
 *   Sliding-window log per (resellerId, endpoint) key.
 *   Separate batch-window log per key to enforce the 20% ceiling.
 *   Serialised promise-chain gate prevents TOCTOU races within a single isolate.
 *   (CF Workers: mcp-server DO serialises per-session; ocs-bridge uses KV for cross-isolate.)
 *
 * NEVER LEAK MONEY:
 *   Set RATE_FLOOR_DISABLED=true ONLY in test environments.
 *   Any process-global flag bypasses all per-endpoint governors.
 */

export type CallPriority = "interactive" | "batch";

// ---------------------------------------------------------------------------
// Endpoint limit table
// ---------------------------------------------------------------------------

/** Per-minute cap for a given OCS method (camelCase). Default: 300/min. */
const ENDPOINT_LIMITS_PER_MIN: Record<string, number> = {
  // Global sentinel (keyed as "__global__")
  __global__: 600,

  // 30/min — tightest endpoint
  subscriberNetworkEventsOverPeriod: 30,

  // 100/min
  subscriberUsageOverPeriod: 100,

  // 150/min
  listLocationZoneElement: 150,
  sendMtSms: 150,

  // 300/min (explicit; also the fallback default)
  modifySubscriberMobilePlan: 300,
  affectRecurringPackageToSubscriber: 300,
  affectSubscriberFakePhoneNumber: 300,
  affectSubscriberRealPhoneNumber: 300,
  esimStatusPerAccount: 300,
  getSubscriberActivePeriod: 300,
  hlrGetBitrate: 300,
  hlrSetBitrate: 300,
  listSubscriber: 300,
  listSubscriberPrepaidPackages: 300,
  moveSubscriberRangeToAccount: 300,
  pushSteeringToSubs: 300,
  setSubscriberTrafficRestrictions: 300,
  getSingleSubscriber: 300,
  getSubscriberLocationByCellId: 300,
};

const DEFAULT_LIMIT_PER_MIN = 300;
const WINDOW_MS = 60_000; // 1 minute

/** Wall-clock throttle per endpoint bucket — avoids JSON log spam on every OCS call. */
const METRIC_EMIT_MIN_INTERVAL_MS = 10_000;

/** Fraction of capacity reserved for batch callers (20%). */
const BATCH_FRACTION = 0.2;

/** Non-reversible id for metrics only (callers may pass API tokens as reseller keys). */
function resellerKeyHash(resellerKey: string | undefined): string {
  if (!resellerKey) return "00000000";
  let h = 5381;
  for (let i = 0; i < resellerKey.length; i++) {
    h = (((h << 5) + h) ^ resellerKey.charCodeAt(i)) >>> 0;
  }
  return h.toString(16).padStart(8, "0");
}

// ---------------------------------------------------------------------------
// Prometheus-style metric emission
// ---------------------------------------------------------------------------

/**
 * Emit a structured metric line for external scraping / CF Analytics Engine.
 * Format: metric{labels} value timestamp_ms
 */
function emitMetric(
  endpoint: string,
  resellerKey: string,
  callsInWindow: number,
  limitPerMin: number,
): void {
  const pct = Math.round((callsInWindow / limitPerMin) * 100);
  const reseller_hash = resellerKeyHash(resellerKey);
  // Structured log: picked up by CF Workers Tail / BetterStack / Prometheus remote-write.
  // stderr is observable in Workers and safe for MCP stdio. stdout is the
  // JSON-RPC protocol in the npm server: one metric line there corrupts the
  // stream and makes clients report "Failed to parse JSONRPC message".
  console.error(
    JSON.stringify({
      metric: "carrier_ocs_calls_per_min",
      endpoint,
      reseller_hash,
      calls_in_window: callsInWindow,
      limit_per_min: limitPerMin,
      utilisation_pct: pct,
      alert: pct >= 80,
      ts: Date.now(),
    }),
  );
  if (pct >= 80) {
    console.warn(
      `[rate-governor] ALERT: ${endpoint} at ${pct}% capacity (${callsInWindow}/${limitPerMin} per min) reseller=${reseller_hash}`,
    );
  }
}

// ---------------------------------------------------------------------------
// Window state
// ---------------------------------------------------------------------------

interface EndpointWindowState {
  /** All call timestamps in the current sliding window (oldest first). */
  log: number[];
  /** Batch-only call timestamps (subset of log). */
  batchLog: number[];
  /** Serialisation gate — one acquire at a time. */
  gate: Promise<void>;
  /** Last `emitMetric` wall time for this bucket (throttles log volume). */
  lastMetricEmitAt: number;
  /**
   * When true, the next sample at ≥80% utilisation should emit immediately
   * (edge-trigger) so alerts are not delayed by the interval throttle.
   */
  metricBelowHighUtil: boolean;
}

// Keyed as `${resellerId}:${endpoint}` (or `${resellerId}:__global__`)
const _endpointWindows = new Map<string, EndpointWindowState>();

function _getEndpointState(key: string): EndpointWindowState {
  let s = _endpointWindows.get(key);
  if (!s) {
    s = {
      log: [],
      batchLog: [],
      gate: Promise.resolve(),
      lastMetricEmitAt: 0,
      metricBelowHighUtil: true,
    };
    _endpointWindows.set(key, s);
  }
  return s;
}

function _prune(log: number[], windowStart: number): number[] {
  // Remove entries older than the window boundary.
  let i = 0;
  while (i < log.length && log[i]! <= windowStart) i++;
  return i > 0 ? log.slice(i) : log;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Acquire one OCS call slot for the given (resellerId, endpoint) pair.
 *
 * Enforces:
 *   1. Per-endpoint limit (from ENDPOINT_LIMITS_PER_MIN table).
 *   2. Global 600/min limit across all endpoints for this reseller.
 *   3. Batch callers capped at 20% of each bucket.
 *
 * Emits throttled `carrier_ocs_calls_per_min` JSON metrics (and warns at ≥80%
 * utilisation when a sample is emitted).
 *
 * No-op when RATE_FLOOR_DISABLED=true (never set in production).
 */
export async function acquireEndpointSlot(
  resellerId: string,
  endpoint: string,
  priority: CallPriority = "interactive",
): Promise<void> {
  // Env bypass — test environments only.
  const proc = (globalThis as Record<string, unknown>)["process"] as
    | { env?: Record<string, string | undefined> }
    | undefined;
  if (
    proc?.env?.["RATE_FLOOR_DISABLED"] === "true" ||
    (globalThis as Record<string, unknown>)["RATE_FLOOR_DISABLED"] === "true"
  ) {
    return;
  }

  // Global first: avoid holding a tight endpoint slot while waiting on the 600/min bucket.
  await _acquireOneSlot(resellerId, "__global__", priority);
  await _acquireOneSlot(resellerId, endpoint, priority);
}

async function _acquireOneSlot(
  resellerId: string,
  endpoint: string,
  priority: CallPriority,
): Promise<void> {
  const bucketKey = `${resellerId}:${endpoint}`;
  const limitPerMin = ENDPOINT_LIMITS_PER_MIN[endpoint] ?? DEFAULT_LIMIT_PER_MIN;
  const batchCap = Math.floor(limitPerMin * BATCH_FRACTION);

  const state = _getEndpointState(bucketKey);

  const ticket = state.gate.then(async () => {
    while (true) {
      const now = Date.now();
      const windowStart = now - WINDOW_MS;

      // Prune expired entries.
      state.log = _prune(state.log, windowStart);
      state.batchLog = _prune(state.batchLog, windowStart);

      const totalInWindow = state.log.length;
      const batchInWindow = state.batchLog.length;

      // Emit metric on a wall-clock interval; also emit immediately when util
      // first crosses ≥80% after having been below (so alerts are not delayed).
      if (totalInWindow > 0 && endpoint !== "__global__") {
        const utilPct = Math.round((totalInWindow / limitPerMin) * 100);
        const intervalOk =
          now - state.lastMetricEmitAt >= METRIC_EMIT_MIN_INTERVAL_MS;
        const enteredHighUtil = utilPct >= 80 && state.metricBelowHighUtil;
        if (intervalOk || enteredHighUtil) {
          emitMetric(endpoint, resellerId, totalInWindow, limitPerMin);
          state.lastMetricEmitAt = now;
          if (utilPct >= 80) {
            state.metricBelowHighUtil = false;
          }
        }
        if (utilPct < 80) {
          state.metricBelowHighUtil = true;
        }
      }

      // Check capacity:
      //   - Global cap: can't exceed limitPerMin regardless of priority.
      //   - Batch cap:  batch callers additionally can't exceed batchCap.
      const globalFull = totalInWindow >= limitPerMin;
      const batchFull = priority === "batch" && batchInWindow >= batchCap;

      if (!globalFull && !batchFull) {
        // Slot available — record call.
        state.log.push(now);
        if (priority === "batch") {
          state.batchLog.push(now);
        }
        return;
      }

      // Determine how long to wait.
      let waitMs: number;
      if (globalFull) {
        // Wait until oldest entry in the full window ages out.
        const oldest = state.log[0]!;
        waitMs = WINDOW_MS - (now - oldest) + 1;
      } else {
        // Batch cap hit — wait until oldest batch entry ages out.
        const oldestBatch = state.batchLog[0]!;
        waitMs = WINDOW_MS - (now - oldestBatch) + 1;
      }

      await new Promise<void>((r) => setTimeout(r, waitMs));
    }
  });

  state.gate = ticket;
  return ticket;
}

/**
 * Returns the per-minute limit for a given OCS method.
 * Exported for testing and monitoring.
 */
export function getLimitForEndpoint(endpoint: string): number {
  return ENDPOINT_LIMITS_PER_MIN[endpoint] ?? DEFAULT_LIMIT_PER_MIN;
}

/**
 * Read-only counts for the current sliding window (same keying as acquireEndpointSlot).
 * Scans timestamps without mutating state — safe for status tools alongside acquires.
 */
export function getRateLimitWindowCounts(
  resellerKey: string,
  endpoint: string,
): { calls_in_window: number; batch_calls_in_window: number } {
  const bucketKey = `${resellerKey}:${endpoint}`;
  const state = _endpointWindows.get(bucketKey);
  if (!state) {
    return { calls_in_window: 0, batch_calls_in_window: 0 };
  }
  const windowStart = Date.now() - WINDOW_MS;
  let calls_in_window = 0;
  for (const ts of state.log) {
    if (ts > windowStart) calls_in_window++;
  }
  let batch_calls_in_window = 0;
  for (const ts of state.batchLog) {
    if (ts > windowStart) batch_calls_in_window++;
  }
  return { calls_in_window, batch_calls_in_window };
}

/** Exposed for testing: reset all endpoint window state. */
export function _resetGovernorForTest(): void {
  _endpointWindows.clear();
}
